// src/routes/AppRoutes.tsx
import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import SiteLayout from "../components/layout/SiteLayout";
import RequireAuth from "../components/auth/RequireAuth";
import RequireAdmin from "../components/auth/RequireAdmin";

import Home from "../pages/Home";
import ProductDetail from "../pages/ProductDetail";
import Lookbook from "../pages/Lookbook";
import Collection from "../pages/Collection";
import Drops from "../pages/Drops";

import CartPage from "../pages/cart/CartPage";
import CheckoutPage from "../pages/cart/CheckoutPage";
import ProfilePage from "../pages/ProfilePage";

import AuthPage from "../pages/auth/AuthPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import PayOSCallback from "../pages/payment/PayOSCallback";
import OrderDetailPage from "../pages/orders/OrderDetailPage";
import PolicyPage from "../pages/Policy/PolicyPage";
import VoucherGameClient from "../pages/VoucherGameClient";
import { useGlobalLoader } from "../providers/LoaderProvider";

/* ======================
   PAGE FADE WRAPPER
====================== */
function PageFade({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isLoading } = useGlobalLoader();

  return (
    <div key={location.pathname} className={isLoading ? "" : "pagefade"}>
      {children}

      <style>{`
        .pagefade { animation: fade .22s ease both; }
        @keyframes fade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* =================================================
         PUBLIC SITE (Header + Footer)
      ================================================= */}
      <Route element={<SiteLayout />}>
        <Route
          path="/"
          element={
            <PageFade>
              <Home />
            </PageFade>
          }
        />

        <Route
          path="/collection"
          element={
            <PageFade>
              <Collection />
            </PageFade>
          }
        />

        <Route
          path="/drops"
          element={
            <PageFade>
              <Drops />
            </PageFade>
          }
        />

        <Route
          path="/policies"
          element={
            <PageFade>
              <PolicyPage />
            </PageFade>
          }
        />

        {/* ✅ Product Detail dùng ID (khớp API /api/Products/client/{id}) */}
        <Route
          path="/product/:id"
          element={
            <PageFade>
              <ProductDetail />
            </PageFade>
          }
        />

        {/* ✅ Backward compat: nếu nơi nào đó vẫn dùng /product/:slug
            → redirect sang /product/:id nếu slug là số
            (tránh link cũ bị 404)
         */}
        <Route path="/product/:slug" element={<RedirectSlugToId />} />

        {/* =============================================
           PRIVATE ROUTES (CẦN LOGIN)
        ============================================= */}
        <Route element={<RequireAuth />}>
          <Route
            path="/cart"
            element={
              <PageFade>
                <CartPage />
              </PageFade>
            }
          />
          <Route
            path="/payment/callback"
            element={
              <PageFade>
                <PayOSCallback />
              </PageFade>
            }
          />

          <Route
            path="/checkout"
            element={
              <PageFade>
                <CheckoutPage />
              </PageFade>
            }
          />
          <Route
            path="/voucher-game"
            element={
              <PageFade>
                <VoucherGameClient />
              </PageFade>
            }
          />

          <Route
            path="/profile"
            element={
              <PageFade>
                <ProfilePage />
              </PageFade>
            }
          />

          <Route
            path="/orders/:id"
            element={
              <PageFade>
                <OrderDetailPage />
              </PageFade>
            }
          />
        </Route>
      </Route>

      {/* =================================================
         EDITORIAL / IMMERSIVE
      ================================================= */}
      <Route path="/lookbook" element={<Lookbook />} />

      {/* =================================================
         AUTH
      ================================================= */}
      <Route
        path="/auth"
        element={
          <PageFade>
            <AuthPage />
          </PageFade>
        }
      />

      <Route
        path="/auth/forgot-password"
        element={
          <PageFade>
            <ForgotPasswordPage />
          </PageFade>
        }
      />

      {/* =================================================
         NOT FOUND (optional nhưng nên có)
      ================================================= */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/* ======================
   Redirect helper:
   /product/:slug -> /product/:id (nếu slug là số)
====================== */
function RedirectSlugToId() {
  const { pathname } = useLocation();
  // pathname dạng "/product/123" hoặc "/product/abc"
  const parts = pathname.split("/");
  const last = parts[parts.length - 1] || "";
  const n = Number(last);

  if (!Number.isNaN(n) && n > 0) {
    return <Navigate to={`/product/${n}`} replace />;
  }

  // nếu slug không phải số => tạm về collection hoặc home
  return <Navigate to="/collection" replace />;
}
